import clsx from 'clsx';
import { Dropdown } from 'components/dropdown';
import type {
  DoseDataFragment,
  DoseUnit,
  MedicineTradeName,
} from 'graphql/types';
import { useEffect, useMemo } from 'react';
import { type FieldErrors, useForm } from 'react-hook-form-6';
import {
  FaPencilAlt,
  FaPlusCircle,
  FaSave,
  FaTrash,
  FaSyringe,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { Handle, type NodeProps, Position } from 'reactflow';
import { buildRoute } from 'utils/routes';
import { Input } from 'components/react-hook-form-6/input';
import { Copyable } from 'components/copyable';

function formatDoseUnit(unit?: DoseUnit): string {
  switch (unit) {
    case 'MILLILITER':
      return 'mL';
    default:
      return '';
  }
}

const medincineTradeNames = [
  'MOUNJARO',
  'OZEMPIC',
  'WEGOVY',
] as const satisfies readonly MedicineTradeName[];

export type Dose = {
  id: string;
  doseId?: string;
  variantId?: string;
  nextNodeId?: string | null;
  intervalDays?: number | null;
  medicineTradeName?: MedicineTradeName;
};

export interface NodeData {
  node: Dose;
  editing: boolean;
  loopSource: boolean;
  loopTargetable: boolean;
  controls?: {
    edit: () => void;
    save: (node: Dose) => void;
    remove: () => void;
    insertLeft: () => void;
    insertRight: () => void;
  };
  variants?: Array<{
    id: string;
    productId: string;
    productName: string;
    name: string;
    sku: string;
  }>;
  doses?: Array<DoseDataFragment>;
}

export function Node(props: NodeProps<NodeData>) {
  const {
    editing,
    loopSource,
    loopTargetable,
    node,
    controls,
    variants,
    doses,
  } = props.data;

  const { reset, handleSubmit, register, control, errors, watch } =
    useForm<Dose>({
      defaultValues: node,
    });

  const medicineTradeName = watch().medicineTradeName;

  useEffect(() => {
    reset(node);
  }, [node, reset]);

  const submitHandler = async (formValues: Dose): Promise<void> => {
    controls?.save(formValues);
  };

  const medincineTradeNameOptions = medincineTradeNames.map((mtr) => ({
    label: mtr,
    value: mtr,
  }));

  const doseOptions = useMemo(
    () =>
      doses
        ?.filter((d) => d.medicineTradeName === medicineTradeName)
        .map((dose) => ({
          value: dose.id,
          label: `${dose.medicineTradeName} - ${dose.quantity} ${formatDoseUnit(
            dose.unit,
          )}`,
        })) || [],
    [doses, medicineTradeName],
  );

  const variantOptions = useMemo(
    () =>
      variants?.map((variant) => ({
        value: variant.id,
        label: `${variant.productName} - ${variant.name}${variant.sku || ''}`,
      })) || [],
    [variants],
  );

  const variant = variants?.find((v) => v.id === node.variantId);
  const dose = doses?.find((d) => d.id === node.doseId);

  let title = 'Dose';
  let icon = <FaSyringe />;
  let content = null;

  content = (
    <div className="flex flex-col text-sm gap-1">
      <div className="flex justify-between text-sm">
        <span className="font-semibold basis-1/3">Variant</span>
        <Link
          className="underline whitespace-nowrap overflow-ellipsis overflow-hidden"
          to={buildRoute.product(variant?.productId ?? '')}
        >
          {variant?.name}
        </Link>
      </div>
      <div className="flex justify-between hover:text-slate-950 text-sm">
        <span className="font-semibold basis-1/3">Variant ID</span>
        <Copyable text={node.variantId ?? ''}>
          {(copied) => (
            <pre className="cursor-pointer">
              {copied ? 'Copied' : node.variantId?.slice(-8)}
            </pre>
          )}
        </Copyable>
      </div>
      <div className="flex justify-between text-sm">
        <span className="font-semibold basis-1/3">Medication</span>
        {dose?.medicineTradeName}
      </div>
      <div className="flex justify-between text-sm">
        <span className="font-semibold basis-1/3">Dose</span>
        {dose?.quantity && dose.unit
          ? `${dose.quantity} ${formatDoseUnit(dose.unit)}`
          : ''}
      </div>
      <div className="flex justify-between text-sm">
        <div>
          <span className="font-semibold basis-1/3">Days before next dose</span>
        </div>
        <span>{node.intervalDays}</span>
      </div>
    </div>
  );

  if (editing) {
    icon = <FaPencilAlt />;
    title = 'Editing';

    const dosetFormErrors: FieldErrors<Dose> = errors;

    content = (
      <div className="flex flex-col gap-2 nodrag nowheel">
        <Dropdown
          name="variantId"
          label="Variant"
          control={control}
          options={variantOptions}
          errorMessage={dosetFormErrors.variantId?.message}
          rules={{
            required: { value: true, message: 'Variant is required' },
          }}
        />
        <Dropdown
          name="medicineTradeName"
          label="Medicine trade name"
          control={control}
          options={medincineTradeNameOptions}
          errorMessage={dosetFormErrors.medicineTradeName?.message}
          rules={{
            required: {
              value: true,
              message: 'Medicine trade name is required',
            },
          }}
        />
        <Dropdown
          name="doseId"
          label="Dose"
          control={control}
          options={doseOptions}
          disabled={!medicineTradeName}
          errorMessage={dosetFormErrors.doseId?.message}
          rules={{
            required: { value: true, message: 'Dose required' },
          }}
        />
        <Input
          name="intervalDays"
          label="Days before next dose"
          type="number"
          ref={register({
            valueAsNumber: true,
            min: { value: 0, message: 'Value must be 0 or greater' },
          })}
          errorMessage={dosetFormErrors?.intervalDays?.message}
        />
      </div>
    );
  }

  return (
    <div
      className={clsx('w-80 text-slate-800 shadow-md border-2', {
        'border-slate-400 bg-slate-100': props.selected,
        'border-slate-200 bg-slate-50': !props.selected,
      })}
    >
      <Handle
        type="target"
        className="!bg-slate-200 !border-slate-400"
        id="left-target"
        position={Position.Left}
      />
      <Handle
        type="target"
        className={clsx('!bg-slate-200 !border-slate-400', {
          '!invisible': !loopTargetable,
        })}
        id="top-target"
        position={Position.Top}
      />
      <Handle
        type="source"
        className={clsx('!bg-slate-200 !border-slate-400', {
          '!invisible': !loopSource,
        })}
        id="top-source"
        position={Position.Top}
      />
      <Handle
        type="source"
        className={clsx('!bg-slate-200 !border-slate-400', {
          '!invisible': loopSource,
        })}
        id="right-source"
        position={Position.Right}
      />
      <div className="px-4 py-2">
        <div className="flex flex-col">
          <div className="flex justify-between items-center pb-2">
            <div className="text-lg items-center flex gap-2 font-semibold capitalize">
              {icon}
              {title}
            </div>
          </div>
          {content}
        </div>
      </div>

      {controls && (
        <div className="flex h-8 cursor-pointer mt-2 border-t-2 border-slate-300">
          <div
            onClick={controls.insertLeft}
            className="basis-1/4 hover:bg-slate-300 bg-slate-200 flex items-center justify-center"
          >
            <FaPlusCircle className="text-slate-600" size={18} />
          </div>
          {editing ? (
            <div
              onClick={handleSubmit(submitHandler)}
              className="basis-1/4 hover:bg-slate-200 bg-slate-50 flex items-center justify-center"
            >
              <FaSave className="text-slate-600" size={18} />
            </div>
          ) : (
            <div
              onClick={controls.edit}
              className="basis-1/4 hover:bg-slate-200 bg-slate-50 flex items-center justify-center"
            >
              <FaPencilAlt className="text-slate-600" size={18} />
            </div>
          )}
          <div
            onClick={controls.remove}
            className="basis-1/4 hover:bg-red-300 bg-red-200 flex items-center justify-center"
          >
            <FaTrash className="text-slate-600" size={18} />
          </div>
          <div
            onClick={controls.insertRight}
            className="basis-1/4 hover:bg-slate-300 bg-slate-200 flex items-center justify-center"
          >
            <FaPlusCircle className="text-slate-600" size={18} />
          </div>
        </div>
      )}
    </div>
  );
}
